import React from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { feedbackTypes, wornInquiry } from "constants/happiness-survey-values";
import { addCacheBusterToUrl } from "helpers/location-helpers";
import { HELP, User } from "routes";

import { fetchProfile } from "actions/user-actions";
import { completeSurvey, addFeedback } from "actions/happiness-survey-actions";
import HeapHelpers from "helpers/heap-helpers";
import { createSelectedSku } from "helpers/sku-helpers";
import Modal from "components/source/shared/modal";
import RtrButton from "components/design-system/rtr-button";

import HappinessSurveyCheckbox from "./happiness-survey-checkbox";
import HappinessSurveyProductGrid from "./happiness-survey-product-grid";
import {
  ProductImageOrientation,
  ProductImageSize,
  getProductImageAltText,
  getProductImageUrlsByOrientation,
} from "helpers/product-image-helpers";
import ProductImagePath from "helpers/product-image-path";
import ProductImage from "components/source/shared/product-image";
import HappinessSurveyFeedback from "./happiness-survey-feedback";
import { useOnLoadFlagOrExperiment } from "components/source/hoc/with-flags-and-experiments";
import { flagsAndExperimentNames } from "rtr-constants";

const propTypes = {
  closeHappinessSurvey: PropTypes.func,
  handleRequestClose: PropTypes.func,
  isModalOpen: PropTypes.bool,
};

function HappinessSurveySubscription({ handleRequestClose, closeHappinessSurvey, isModalOpen }) {
  const dispatch = useDispatch();

  const happinessSurvey = useSelector(state => state.happinessSurvey);
  const isMasquerading = useSelector(state => state.userData?.isMasquerading);
  const [wornItemsBookingIds, setWornItemsBookingIds] = React.useState([]);
  const [hasClickedSelectNone, setHasClickedSelectNone] = React.useState(false);
  const [hasClickedSelectAll, setHasClickedSelectAll] = React.useState(false);
  const [showFeedbackScreen] = useOnLoadFlagOrExperiment(flagsAndExperimentNames.RSV_1704_HAPPINESS_SURVEY_REVAMP_2_0);
  const [screenStep, setScreenStep] = React.useState(0);

  if (isMasquerading) {
    return null;
  }

  React.useEffect(() => {
    if (isModalOpen) {
      dispatch(fetchProfile());
    }
  }, [isModalOpen]);

  React.useEffect(() => {
    if (wornItemsBookingIds.length === happinessSurvey.itemSurveys.length) {
      setHasClickedSelectAll(true);
    } else {
      setHasClickedSelectAll(false);
    }

    updateWearRate();
  }, [wornItemsBookingIds]);

  const allItemsSelected = wornItemsBookingIds.length === happinessSurvey.itemSurveys.length;
  const canSubmit = wornItemsBookingIds.length > 0 || hasClickedSelectNone;

  const updateWearRate = () => {
    happinessSurvey.itemSurveys.forEach(itemSurvey => {
      if (wornItemsBookingIds.includes(itemSurvey.bookingId)) {
        dispatch(addFeedback(wornInquiry.WORE_IT_ONCE, itemSurvey.bookingId, feedbackTypes.wornInquiry));
      } else {
        dispatch(addFeedback(wornInquiry.DIDNT_WEAR_IT, itemSurvey.bookingId, feedbackTypes.wornInquiry));
      }
    });
  };

  const handleStartScreenItemTapped = bookingId => {
    //  unselecting an item
    setHasClickedSelectNone(false);
    if (Array.isArray(wornItemsBookingIds) && wornItemsBookingIds.includes(bookingId)) {
      setWornItemsBookingIds(prevIds => prevIds.filter(item => item !== bookingId));
    }
    //  selecting an item
    else {
      setWornItemsBookingIds(prevIds => [...prevIds, bookingId]);
    }
  };
  const handleStartScreenSelectAllTapped = () => {
    setHasClickedSelectNone(false);

    if (allItemsSelected) {
      setWornItemsBookingIds([]);
      setHasClickedSelectAll(false);
    } else {
      setWornItemsBookingIds(happinessSurvey?.itemSurveys?.map(item => item.bookingId));
      setHasClickedSelectAll(true);
    }
  };

  const handleStartScreenClearTapped = () => {
    setWornItemsBookingIds([]);
    setHasClickedSelectAll(false);
    setHasClickedSelectNone(false);
  };

  const handleStartScreenSelectNoneTapped = () => {
    setWornItemsBookingIds([]);
    setHasClickedSelectNone(!hasClickedSelectNone);
  };

  const submitSurvey = () => {
    if (!canSubmit) return;
    happinessSurvey.itemSurveys.forEach(itemSurvey => {
      dispatch(
        completeSurvey(itemSurvey.bookingId, (getState, completedSurvey) => {
          const eventData = {
            style_name: itemSurvey?.product?.id,
            sku: createSelectedSku(itemSurvey?.product?.id, itemSurvey?.size, itemSurvey?.product?.skus)?.id,
            rating: completedSurvey.rating,
            number_of_photos: completedSurvey.photoIds.length,
            has_review_headline: completedSurvey.caption?.length > 0,
            has_review_description: completedSurvey.reviewContent?.length > 0,
            survey_feedback: JSON.stringify(completedSurvey.surveyFeedbacks.map(feedback => feedback.feedback)),
          };

          HeapHelpers.fireHeapEvent("sf-happiness-survey-submit-review-of-one-item", eventData);
        })
      );
    });

    setScreenStep(2);
  };

  const whichItemsDidYouWear = () => (
    <div data-test-id="happiness-survey-start-screen">
      <div data-test-id="happiness-survey-title">
        <h2 className="font-sans text-h4">Just one question!</h2>
        <p className="text-darkGray mt-2">This helps us give you more of what you love.</p>
      </div>
      <hr className="my-4 border-lighterGray" />
      <p data-test-id="happiness-survey-instruction-text" className="semibold mb-4">
        Which items did you wear?
      </p>
      <HappinessSurveyCheckbox
        canSelectAll={!hasClickedSelectAll}
        canClearAll={wornItemsBookingIds.length > 0 || hasClickedSelectNone}
        isSelectNoneChecked={hasClickedSelectNone}
        handleSelectAll={handleStartScreenSelectAllTapped}
        handleSelectNone={handleStartScreenSelectNoneTapped}
        handleClearAll={handleStartScreenClearTapped}
      />
      <HappinessSurveyProductGrid
        itemSurveys={happinessSurvey?.itemSurveys}
        selectedItems={wornItemsBookingIds}
        onClickItem={handleStartScreenItemTapped}
        isSelectNoneChecked={hasClickedSelectNone}
      />
    </div>
  );

  const fallBackThumbnailImgSrc = productData =>
    productData?.images?.[ProductImageOrientation.NO_MODEL.path]
      ? ProductImagePath + productData.images[ProductImageOrientation.NO_MODEL.path]?.[ProductImageSize.x480.path]
      : ProductImagePath + productData?.images?.[ProductImageOrientation.FRONT.path]?.[ProductImageSize.x480.path];

  const thankYou = () => (
    <div data-test-id="happiness-survey-final-screen" className="px-2">
      <h2 data-test-id="happiness-survey-title-final" className="font-sans text-h4 my-4 text-center">
        Thank you!
      </h2>
      <hr className="mb-6 border-lighterGray" />
      <p className="font-semibold mb-4">Any problems with these items?</p>
      <div className="grid grid-cols-5 gap-1">
        {happinessSurvey.itemSurveys.map(item => {
          return (
            <ProductImage
              key={item.bookingId}
              className="rounded-lg"
              altText={getProductImageAltText(item?.product?.displayName, item?.product?.designer?.displayName)}
              imageGroup={getProductImageUrlsByOrientation(item?.product?.images, [
                ProductImageOrientation.NO_MODEL.path,
              ])}
              imageSize={ProductImageSize.x183}
              fallbackSrc={fallBackThumbnailImgSrc(item?.product)}
            />
          );
        })}
      </div>
      <RtrButton style="secondary" className="font-semibold mt-8" href={`${addCacheBusterToUrl(HELP.CONTACT)}`} slim>
        Chat with Us
      </RtrButton>
      <hr className="my-8 border-lighterGray" />
      <p className="mb-2">Catch up on reviews</p>
      <p className="text-darkGray mb-6">
        This helps others find their perfect fit (and they&#39;ll do the same for you).
      </p>
      <RtrButton style="secondary" className="font-semibold" href={User.OrderHistory} slim>
        Review Past Items
      </RtrButton>
    </div>
  );
  const nextStep = () => {
    if (showFeedbackScreen) {
      setScreenStep(1);
    } else {
      updateWearRate();
      submitSurvey();
      setScreenStep(2);
    }
  };
  const whichItemsDidYouWearCta = () => {
    let ctaText = "tap items to select";

    if (canSubmit) {
      ctaText = `Submit | ${wornItemsBookingIds.length} selected`;
    }

    return (
      <RtrButton disabled={!canSubmit} fullWidth onClick={nextStep}>
        {ctaText}
      </RtrButton>
    );
  };

  const feedbackCta = () => {
    const hasFeedback = happinessSurvey.itemSurveys?.some(
      item => Array.isArray(item.surveyFeedbacks) && item.surveyFeedbacks.length > 1
    );
    const text = hasFeedback ? "Submit With Feedback" : "Submit Without Feedback";

    const handleClick = () => {
      updateWearRate();
      submitSurvey();
      setScreenStep(2);
    };

    return (
      <RtrButton fullWidth onClick={handleClick}>
        {text}
      </RtrButton>
    );
  };

  const thankYouCta = () => {
    return (
      <RtrButton fullWidth onClick={closeHappinessSurvey}>
        Done
      </RtrButton>
    );
  };

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={handleRequestClose}
      className="h-full sm:h-4/5 !p-0 flex flex-col justify-between">
      <div data-test-id="happiness-survey-modal-content" className="flex flex-col h-full">
        <div className={`pt-6 px-4 grow ${screenStep === 1 ? "bg-offWhite" : ""}`}>
          {screenStep === 0 && whichItemsDidYouWear()}
          {screenStep === 1 && showFeedbackScreen && <HappinessSurveyFeedback onBack={() => setScreenStep(0)} />}
          {screenStep === 2 && thankYou()}
        </div>

        <div
          data-test-id="happiness-survey-footer"
          className="w-full bg-white p-4 sm:py-6 border-t border-lighterGray sticky bottom-0 shadow-top-md">
          {screenStep === 0 && whichItemsDidYouWearCta()}
          {screenStep === 1 && showFeedbackScreen && feedbackCta()}
          {screenStep === 2 && thankYouCta()}
        </div>
      </div>
    </Modal>
  );
}

HappinessSurveySubscription.propTypes = propTypes;
export default HappinessSurveySubscription;
